import { ReactNode, useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { ManualSheet } from '@/components/ManualSheet';
import { Button } from '@/components/ui/button';
import { Menu, X, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MembershipAlertsBanner } from '@/components/MembershipAlertsBanner';
import { TrialOverlay } from '@/components/TrialOverlay';
import { TrialBanner } from '@/components/TrialBanner';
import { TermsModal } from '@/components/TermsModal';

interface DashboardLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}

export function DashboardLayout({ children, title, subtitle, actions }: DashboardLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isManualOpen, setIsManualOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background flex relative">
      <TrialOverlay />
      <TermsModal />

      {/* Sidebar Overlay for Mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0",
        isSidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <Sidebar onClose={() => setIsSidebarOpen(false)} />
      </div>

      {/* Main Content */}
      <main className="flex-1 min-w-0 transition-all duration-300">
        {/* Header */}
        <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
          <div className="flex min-h-[4rem] items-center justify-between px-4 lg:px-6 py-2">
            <div className="flex items-center gap-3 overflow-hidden mr-2">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden shrink-0"
                onClick={() => setIsSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-base lg:text-xl font-semibold text-foreground truncate leadng-tight">
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-[10px] lg:text-sm text-muted-foreground truncate leading-tight mt-0.5">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
            {actions && (
              <div className="flex items-center gap-1.5 lg:gap-3 shrink-0">
                {actions}
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsManualOpen(true)}
              className="shrink-0 text-muted-foreground hover:text-foreground"
              aria-label="Manual de uso"
              title="Manual de uso"
            >
              <HelpCircle className="h-5 w-5" />
            </Button>
          </div>
        </header>

        {/* Trial Status Banner */}
        <TrialBanner />

        {/* Membership Alerts Banner */}
        <MembershipAlertsBanner />

        {/* Page Content */}
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>

      <ManualSheet open={isManualOpen} onOpenChange={setIsManualOpen} />
    </div>
  );
}
